import {
  MicrobiomeAgeBandDatum,
  MicrobiomeAgeOverTimeChartModel,
  MicrobiomeAgeRange,
  MicrobiomeAgeSample,
} from './types/microbiome-age.types';

// Plot geometry used to reserve dot-radius room in data units. The sample dot
// spans 24px (see SampleDot), so ~12px each side; the plot is ~182px tall
// (250 height − 16 top − 16 bottom margin − 36 x-axis).
const DOT_RADIUS_PX = 12;
const PLOT_HEIGHT_PX = 182;

export function buildMicrobiomeAgeOverTimeChart(
  ranges: MicrobiomeAgeRange[],
  samples: MicrobiomeAgeSample[],
): MicrobiomeAgeOverTimeChartModel | null {
  if (!ranges.length || !samples.length) return null;

  const sorted = [...samples].sort((a, b) => a.age - b.age);
  const firstAge = sorted[0].age;
  const lastAge = sorted[sorted.length - 1].age;

  const xMin = Math.floor(firstAge) - 1;
  const xMax = Math.max(Math.floor(firstAge) + 5, Math.ceil(lastAge) + 1);

  // Clamp to the table's 18–100 coverage (flat-extrapolate beyond it).
  const byAge = new Map(ranges.map(r => [r.age, r]));
  const tableAges = ranges.map(r => r.age);
  const minAge = Math.min(...tableAges);
  const maxAge = Math.max(...tableAges);
  const rowAt = (age: number) =>
    byAge.get(Math.min(maxAge, Math.max(minAge, age)))!;

  const lowers: number[] = [];
  const uppers: number[] = [];
  const bounds = [];
  for (let age = xMin; age <= xMax; age++) {
    const row = rowAt(age);
    const lower = Number(row.lower_bound);
    const mid = Number(row.mid_bound);
    const upper = Number(row.upper_bound);
    lowers.push(lower);
    uppers.push(upper);
    bounds.push({ age, lower, mid, upper });
  }

  // Always extend past the outer thresholds so the open-ended Low and High
  // tiers stay visible; also include the samples and the chronological-age
  // range so the dots and the y=x "your age" line never clip.
  const sampleValues = sorted.map(s => s.microbiomeAge);
  const minSample = Math.min(...sampleValues);
  const maxSample = Math.max(...sampleValues);
  const minLower = Math.min(...lowers);
  const maxUpper = Math.max(...uppers);
  const tierPad = Math.max(1, (maxUpper - minLower) * 0.2);

  // Extend the y-range past the most extreme sample so its dot's radius isn't
  // clipped at the plot edge (the dot keeps its true value; the axis makes room).
  const yLow = Math.min(minLower - tierPad, minSample, xMin);
  const yHigh = Math.max(maxUpper + tierPad, maxSample, xMax);
  const dotPad = (DOT_RADIUS_PX * (yHigh - yLow)) / PLOT_HEIGHT_PX;
  const yMin = Math.floor(Math.min(yLow, minSample - dotPad));
  const yMax = Math.ceil(Math.max(yHigh, maxSample + dotPad));

  const data: MicrobiomeAgeBandDatum[] = bounds.map(
    ({ age, lower, mid, upper }) => ({
      age,
      lower,
      mid,
      upper,
      diagonal: age,
      greenBand: [yMin, lower],
      greyBand: [lower, mid],
      orangeBand: [mid, upper],
      redBand: [upper, yMax],
    }),
  );

  const xTicks = [];
  for (let age = xMin; age <= xMax; age++) xTicks.push(age);

  return {
    data,
    samples: sorted,
    xDomain: [xMin, xMax],
    yDomain: [yMin, yMax],
    xTicks,
  };
}
